import React, { useId, useRef, useState } from 'react';
import { ExplainMetricButton } from '../../../features/abf/copilot/AbfCopilotLayout';
import { Badge, Card, Small, Stack, human, show } from '../CreditWorkflowUI';
import { DecisionBar, Journey, Layout, Note, Pair, Rail, Strip, Surface, TaskButton, Work } from './creditReviewStyles';

type ReviewView = 'case' | 'evidence' | 'decision' | 'record';
interface PacketSummary {
  packetStatus?: string;
  version?: number;
  readinessScore?: number | null;
  analyst?: string;
  recommendation?: string;
}
interface Props {
  assetLabel?: string;
  packet?: PacketSummary | null;
  currentReady: number;
  checkCount: number;
  investmentCase: React.ReactNode;
  evidence: React.ReactNode;
  reviewRecord: React.ReactNode;
  activity: React.ReactNode;
  actions: React.ReactNode;
  editor: React.ReactNode;
}

const views: { id: ReviewView; category: string; title: string; description: string }[] = [
  { id: 'case', category: '01 · Investment case', title: 'Thesis & recommendation', description: 'Prepare the case the investment committee will review.' },
  { id: 'evidence', category: '02 · Evidence', title: 'Sources & readiness', description: 'Review the source checks and evidence included when you save the packet.' },
  { id: 'decision', category: '03 · Decision', title: 'Review & submit', description: 'Review the saved packet and its recorded decision.' },
  { id: 'record', category: '04 · Record', title: 'History & investment records', description: 'Inspect packet activity and understand the investment hand-off.' },
];

/** Presentation only: all business state, permissions, editors and actions belong to the existing operating tab. */
const CreditUnderwritingWorkspace: React.FC<Props> = p => {
  const [view, setView] = useState<ReviewView>('case');
  const work = useRef<HTMLElement>(null);
  const headingId = useId();
  const current = views.find(item => item.id === view)!;
  const status = p.packet?.packetStatus;
  const stage = status === 'APPROVED' ? 3 : status === 'SUBMITTED' ? 2 : 1;
  const gaps = p.checkCount - p.currentReady;
  const state = !p.packet ? 'Prepare the investment case'
    : status === 'APPROVED' ? 'IC packet approved'
      : status === 'SUBMITTED' ? 'Submitted for IC review' : 'Prepare the saved packet for review';
  const select = (next: ReviewView) => {
    setView(next);
    if (window.matchMedia('(max-width: 1000px)').matches) work.current?.scrollIntoView({ block: 'start' });
  };

  return <Surface aria-label={`${p.assetLabel || 'Private Credit'} underwriting workspace`}>
    <Journey aria-label="IC packet progress">
      {[
        ['Prepare the investment case', 'Thesis, risks and supporting evidence'],
        ['Submit for IC review', 'Review the saved packet and recommendation'],
        ['Record the decision', 'IC approval is separate from trade approval'],
      ].map(([title, description], index) => <li key={title} aria-current={stage === index + 1 ? 'step' : undefined}>
        <span className="step" aria-hidden="true">{index + 1}</span><div><strong>{title}</strong><small>{description}</small></div>
      </li>)}
    </Journey>
    <Strip aria-label="Underwriting context">
      <div><dt>Current packet</dt><dd>{p.packet ? `Version ${p.packet.version}` : 'Not prepared'}</dd><small>{human(status || 'Not prepared')}</small></div>
      <div><dt>Saved readiness <ExplainMetricButton scope="current" metric="READINESS" /></dt><dd>{p.packet?.readinessScore != null ? `${p.packet.readinessScore}%` : '—'}</dd><small>Recorded on the saved packet</small></div>
      <div><dt>Current checks</dt><dd>{p.currentReady} / {p.checkCount}</dd><small>{gaps ? `${gaps} source check${gaps === 1 ? '' : 's'} need attention` : 'Current source checks ready'}</small></div>
      <div><dt>Analyst</dt><dd>{show(p.packet?.analyst)}</dd><small>{p.packet ? human(p.packet.recommendation) : 'No saved recommendation'}</small></div>
    </Strip>
    <Layout>
      <Rail aria-label="Underwriting review sections">
        <div className="heading"><h2>Packet review</h2><p>The case, its evidence and the decision.</p></div>
        <div className="tasks">{views.map(item => <TaskButton key={item.id} type="button" aria-pressed={view === item.id}
          aria-controls={headingId} onClick={() => select(item.id)}>
          <span className="category">{item.category}</span><strong>{item.title}</strong>
          <span className="reason">{item.id === 'case' ? human(status || 'Not prepared')
            : item.id === 'evidence' ? `${p.currentReady} of ${p.checkCount} current checks ready`
              : item.id === 'decision' ? 'Saved packet & review record' : 'Recorded packet activity'}</span>
        </TaskButton>)}</div>
      </Rail>
      <Work ref={work} id={headingId} aria-label={current.title}>
        <div className="work-heading"><div><h2>{current.title}</h2><p>{current.description}</p></div><Badge value={status || 'Not prepared'} /></div>
        {p.editor && <div className="editor">{p.editor}</div>}
        {/* Keep evidence and editors mounted so rail navigation never discards unsaved input. */}
        <Pair $paired={view === 'case'}>
          <div hidden={view !== 'case'}>{p.investmentCase}</div>
          <div hidden={view !== 'case' && view !== 'evidence'}>{p.evidence}</div>
          <div hidden={view !== 'decision'}><Stack>
            <Card><h3>Review the saved packet</h3><Small>Submission uses the saved version; current checklist edits must first be saved.</Small>
              <p>Recommendation: {p.packet ? human(p.packet.recommendation) : 'Not recorded'}</p>
              <p>Saved readiness: {p.packet?.readinessScore != null ? `${p.packet.readinessScore}%` : 'Not recorded'}</p>
            </Card>
            {p.reviewRecord}
            <Note><strong>IC approval records an investment decision.</strong> Acquisition, trade approval, execution and posting remain separate actions in the existing trade lifecycle.</Note>
          </Stack></div>
          <div hidden={view !== 'record'}><Stack>{p.activity}
            <Card><h3>From decision to ownership</h3><Small>Each record retains its own authority.</Small>
              <p>IC packet → Acquisition ticket → Blotter → Posting → Official IBOR</p>
              <Small>Use the facility header’s Acquisition Ticket, Blotter and IBOR Exposure actions to inspect the existing investment records.</Small>
            </Card>
          </Stack></div>
        </Pair>
        <DecisionBar aria-label="IC packet actions">
          <div className="state"><strong>{state}</strong><span>{p.editor ? 'Finish or cancel the open editor before starting another action.' : 'Existing permissions and saved-packet controls apply.'}</span></div>
          {p.actions}
        </DecisionBar>
      </Work>
    </Layout>
  </Surface>;
};

export default CreditUnderwritingWorkspace;
